import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    CardActions,
    Divider,
    Alert
} from '@mui/material';
import { InvoiceRequestDialog } from '../InvoiceRequest/InvoiceRequestDialog';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { constructionService } from '../../services/construction';
import { CompletedWorkWithoutInvoiceRequest } from '../../types/construction';
import { useUser } from '../../hooks/useUser';


const CompletedWorksWithoutInvoiceSection: React.FC = () => {
    const [completedWorks, setCompletedWorks] = useState<CompletedWorkWithoutInvoiceRequest[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState<{ [key: number]: boolean }>({});

    // 請求書作成依頼ダイアログ用の状態変数を追加
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState<boolean>(false);
    const [selectedWorkId, setSelectedWorkId] = useState<number | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // ユーザー情報を取得
    const { userInfo, isLoading: isUserLoading } = useUser();

    useEffect(() => {
        // ユーザー情報の読み込み完了後に工事情報を取得
        if (!isUserLoading && userInfo) {
            fetchCompletedWorksWithoutInvoice();
        }
    }, [isUserLoading, userInfo]);


    const fetchCompletedWorksWithoutInvoice = async () => {
        if (!userInfo || !userInfo.ID) {
            setError('ユーザー情報が取得できません');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // ユーザーIDを文字列に変換して渡す
            const userName = userInfo.lastName.toString();
            // const userName = "配川";
            // console.log('請求書依頼未作成の完了工事取得：ユーザー名', userName);

            const works = await constructionService.getCompletedWorksWithoutInvoiceRequestByUserId(userName);
            setCompletedWorks(works);
        } catch (err) {
            setError('請求書依頼未作成の完了工事リストの取得に失敗しました。');
            console.error('請求書依頼未作成の完了工事リストの取得エラー:', err);
        } finally {
            setLoading(false);
        }
    };

    // const handleCreateInvoiceRequest = async (id: number) => {
    //     if (!userInfo || !userInfo.ID) {
    //         setError('ユーザー情報が取得できません');
    //         return;
    //     }

    //     // 処理中のIDを記録
    //     setProcessing(prev => ({ ...prev, [id]: true }));

    //     try {
    //         const userId = userInfo.ID.toString();
    //         await constructionService.createInvoiceRequestWithUserId(id, userId);

    //         // 成功したら一覧から削除
    //         setCompletedWorks(prev => prev.filter(work => work.ID !== id));
    //     } catch (err) {
    //         console.error('請求書依頼作成エラー:', err);
    //         setError('請求書依頼の作成に失敗しました。');
    //     } finally {
    //         // 処理完了したIDを処理中から削除
    //         setProcessing(prev => {
    //             const newState = { ...prev };
    //             delete newState[id];
    //             return newState;
    //         });
    //     }
    // };


    // 請求書作成依頼ボタンのハンドラを修正
    const handleCreateInvoiceRequest = (id: number) => {
        setSelectedWorkId(id);
        setInvoiceDialogOpen(true);
    };

    // 請求書作成依頼成功時のコールバック
    const handleInvoiceRequestSuccess = () => {
        if (selectedWorkId) {
            // 成功したら一覧から削除
            setCompletedWorks(prev => prev.filter(work => work.ID !== selectedWorkId));
        }
    };



    // 日付をフォーマットする関数
    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'yyyy年MM月dd日', { locale: ja });
        } catch (error) {
            return '日付なし';
        }
    };

    // 金額フォーマット
    const formatAmount = (amount: number) => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
    };

    // データが存在しない場合は何も表示しない
    if (!loading && completedWorks.length === 0 && !error) {
        return null;
    }

    // モバイル用の表示
    const renderMobileView = () => (
        <Box sx={{ width: '100%' }}>
            {completedWorks.map((work) => (
                <Card
                    key={`card-${work.ID}`}
                    sx={{
                        mb: 2,
                        border: '1px solid #ffcc80',
                        backgroundColor: '#fff8e1'
                    }}
                >
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {work.customerName} - {work.siteName}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            {work.constructionDetails || work.workContent || '(工事内容なし)'}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">開始日:</Typography>
                            <Typography variant="body2">{work.startDate_str || '(開始日なし)'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">完了日:</Typography>
                            <Typography variant="body2">{work.completionDate_str || work.completionDate || '(完了日なし)'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">見積金額:</Typography>
                            <Typography variant="body2" fontWeight="bold">{formatAmount(work.estimatedAmount)}</Typography>
                        </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            disabled={!!processing[work.ID]}
                            onClick={() => handleCreateInvoiceRequest(work.ID)}
                            sx={{ minWidth: '120px' }}
                        >
                            {processing[work.ID] ? <CircularProgress size={24} color="inherit" /> : '請求書依頼作成'}
                        </Button>
                    </CardActions>
                </Card>
            ))}
        </Box>
    );


    // PC用の表示
    const renderDesktopTable = () => (
        <TableContainer component={Paper} sx={{ border: '1px solid #ffcc80', backgroundColor: '#fff8e1' }}>
            <Table>
                <TableHead sx={{ backgroundColor: '#ffcc80' }}>
                    <TableRow>
                        <TableCell>顧客名</TableCell>
                        <TableCell>現場名</TableCell>
                        <TableCell>工事内容</TableCell>
                        <TableCell>開始日</TableCell>
                        <TableCell>完了日</TableCell>
                        <TableCell>見積金額</TableCell>
                        <TableCell align="center">操作</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {completedWorks.map((work) => (
                        <TableRow key={`row-${work.ID}`}>
                            <TableCell>{work.customerName}</TableCell>
                            <TableCell>{work.siteName}</TableCell>
                            <TableCell>{work.constructionDetails || work.workContent || '-'}</TableCell>
                            <TableCell>{work.startDate_str || '-'}</TableCell>
                            <TableCell>{work.completionDate_str || work.completionDate || '-'}</TableCell>
                            <TableCell>{formatAmount(work.estimatedAmount)}</TableCell>
                            <TableCell align="center">
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    disabled={!!processing[work.ID]}
                                    onClick={() => handleCreateInvoiceRequest(work.ID)}
                                    sx={{ minWidth: '120px' }}
                                >
                                    {processing[work.ID] ? <CircularProgress size={24} color="inherit" /> : '請求書依頼作成'}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );


    return (
        <Box sx={{ mb: 4 }}>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            ) : (
                <>
                    <Typography
                        variant="h6"
                        component="h2"
                        color="warning.dark"
                        gutterBottom
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            borderLeft: '4px solid #f57c00',
                            pl: 1,
                            mb: 2
                        }}
                    >
                        請求書依頼未作成の完了工事
                    </Typography>

                    {isMobile ? renderMobileView() : renderDesktopTable()}
                </>
            )}

            {/* 請求書作成依頼ダイアログを追加 */}
            <InvoiceRequestDialog
                open={invoiceDialogOpen}
                onClose={() => setInvoiceDialogOpen(false)}
                constructionId={selectedWorkId || 0}
                onSuccess={handleInvoiceRequestSuccess}
            />


        </Box>
    );
};

export default CompletedWorksWithoutInvoiceSection;